import type { CityConfig } from "../city-template/types";

export const stLouisConfig: CityConfig = {
  city: "St. Louis",
  state: "Missouri",
  stateAbbr: "MO",
  slug: "st-louis",
  route: "/cities/st-louis",
  title: "Emergency Passport St. Louis, MO | Fast Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in St. Louis, MO — as fast as 24 hours. Fast passport renewal, same-day help, and lost passport replacement. A+ BBB rated, registered State Dept. courier serving St. Louis County and the metro area.",
  h1: "Emergency & Expedited Passport Services in St. Louis, MO",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport, fast passport renewal, or same-day help? We help St. Louis and Eastern Missouri travelers get their urgent passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=St.%20Louis%20skyline%20at%20golden%20hour%20with%20Gateway%20Arch%20monument%20prominently%20featured%2C%20downtown%20buildings%20reflecting%20warm%20sunset%20light%2C%20Mississippi%20River%20in%20foreground%2C%20warm%20amber%20and%20orange%20tones%20across%20the%20sky%2C%20professional%20cityscape%20photography%2C%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20clean%20modern%20urban%20scene%20with%20iconic%20architecture&width=1800&height=700&seq=stl-hero-001&orientation=landscape",
  heroImageAlt: "St. Louis skyline at golden hour with Gateway Arch and downtown buildings",
  geoPosition: "38.6270;-90.1994",
  geoPlacename: "St. Louis, Missouri",
  lastModified: "2026-06-02",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "101 W Congress Pkwy, Chicago, IL 60605",
    distance: "Chicago is the nearest regional passport agency — approximately 5 hours from St. Louis by car. Fast Passport Center eliminates the need for this drive with our registered courier service.",
  },
  nearbyCitiesZips: [
    { name: "Downtown St. Louis", zips: "63101–63103" },
    { name: "Clayton", zips: "63105" },
    { name: "Chesterfield", zips: "63005, 63017" },
    { name: "Florissant", zips: "63031, 63033" },
    { name: "Kirkwood", zips: "63122" },
    { name: "Ballwin", zips: "63011, 63021" },
    { name: "St. Charles", zips: "63301, 63303" },
    { name: "O'Fallon", zips: "63366, 63368" },
    { name: "Wentzville", zips: "63385" },
    { name: "Wildwood", zips: "63005, 63011, 63021, 63038, 63040, 63069" },
    { name: "Belleville", zips: "62220, 62221, 62223, 62226" },
    { name: "Alton", zips: "62002" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent St. Louis departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Kansas City", route: "/cities/kansas-city" },
    { name: "Emergency passport help in Springfield", route: "/cities/springfield-mo" },
    { name: "Urgent passport services in Columbia", route: "/cities/columbia-mo" },
    { name: "Expedited passport services in Missouri", route: "/states/missouri" },
    { name: "Fast passport renewal in Chicago", route: "/states/illinois" },
    { name: "Passport courier services in Nashville", route: "/cities/nashville" },
    { name: "Rush passport help in Indianapolis", route: "/cities/indianapolis" },
  ],
  fpcOfficeNote: "Fast Passport Center serves St. Louis remotely via secure courier. We do not have a walk-in office in St. Louis — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for St. Louis travelers.",
  fpcOfficeDistance: "Serving all of St. Louis City and St. Louis County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout the entire St. Louis metro area. From downtown STL to Chesterfield, from Florissant to St. Charles — we help St. Louis travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all St. Louis neighborhoods and nearby cities including Chesterfield, Florissant, Kirkwood, Ballwin, St. Charles, and O'Fallon. If you are in St. Louis County or across the river in Illinois, we have you covered.",
  howItWorksIntro: "Most St. Louis travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait for standard mail. Our emergency and expedited passport process is designed for St. Louis travelers who need fast, reliable results.",
  mistakesIntro: "With Lambert Airport serving as a major international hub, St. Louis travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "St. Louis travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in St. Louis? Here are the most common ones we hear from St. Louis County travelers.",
  statePageRoute: "/states/missouri",
  statePageName: "Missouri",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For St. Louis travelers with immediate departure needs — as fast as 24 hours for urgent international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jennifer M.",
      location: "Chesterfield, MO",
      rating: 5,
      text: "My flight to London was in 4 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Absolutely saved my business trip.",
      service: "Emergency Passport",
    },
    {
      name: "David K.",
      location: "Clayton, MO",
      rating: 5,
      text: "I needed to renew my passport for a family vacation to Italy. The process was smooth, they reviewed my documents, and I had my passport in 5 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Maria R.",
      location: "Florissant, MO",
      rating: 5,
      text: "Lost my passport the week before my cruise to the Caribbean. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Thomas B.",
      location: "St. Charles, MO",
      rating: 5,
      text: "As a frequent international traveler from St. Louis, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional, fast, and reliable every time.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "stl-1",
      name: "St. Louis Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1720 Market St",
      city: "St. Louis",
      state: "MO",
      zip: "63103",
      phone: "(314) 436-1530",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown St. Louis",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "stl-2",
      name: "Clayton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7720 Forsyth Blvd",
      city: "Clayton",
      state: "MO",
      zip: "63105",
      phone: "(314) 862-0124",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from Clayton business district",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "stl-3",
      name: "Chesterfield Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "16235 Baxter Rd",
      city: "Chesterfield",
      state: "MO",
      zip: "63017",
      phone: "(636) 532-9525",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Chesterfield Mall",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for West County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "stl-4",
      name: "St. Charles City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "200 N 2nd St",
      city: "St. Charles",
      state: "MO",
      zip: "63301",
      phone: "(636) 949-3200",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "0.2 miles from historic St. Charles",
      website: "https://www.stcharlescitymo.gov/",
      websiteLabel: "St. Charles City Clerk",
      notes: "Accepts walk-ins but appointments preferred. Photo services available on-site.",
      icon: "ri-government-line",
    },
    {
      id: "stl-5",
      name: "Florissant Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1300 Howdershell Rd",
      city: "Florissant",
      state: "MO",
      zip: "63031",
      phone: "(314) 831-6700",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1 mile from Florissant City Hall",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for North County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "stl-6",
      name: "St. Louis County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "41 S Central Ave",
      city: "Clayton",
      state: "MO",
      zip: "63105",
      phone: "(314) 615-2667",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.3 miles from Clayton downtown",
      website: "https://www.stlouisco.com/",
      websiteLabel: "St. Louis County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5-hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "St. Louis photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at St. Louis acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many STL applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many St. Louis families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "St. Louis travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in St. Louis." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your St. Louis address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in St. Louis?",
      a: "Fast Passport Center offers emergency passport processing in St. Louis in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for St. Louis travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in St. Louis?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel within 72 hours. The nearest agency to St. Louis is in Chicago, a 5-hour drive. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to St. Louis residents.",
    },
    {
      q: "Where can I get a fast passport renewal in St. Louis?",
      a: "Fast Passport Center offers fast passport renewal in St. Louis with no office visit required. We serve all St. Louis neighborhoods including Clayton, Chesterfield, Florissant, Kirkwood, and beyond. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in St. Louis?",
      a: "The fastest way to get an urgent passport in St. Louis is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing (8–11 weeks) or even government expedited mail (2–3 weeks).",
    },
    {
      q: "How fast can I get a passport in St. Louis?",
      a: "With Fast Passport Center's emergency courier service, St. Louis travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Chicago Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in St. Louis?",
      a: "Popular options include the St. Louis Main Post Office (63103), Clayton Post Office (63105), Chesterfield Post Office (63017), and St. Charles City Clerk (63301). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in St. Charles or O'Fallon?",
      a: "We serve all of St. Louis County and surrounding areas including St. Charles, O'Fallon, Wentzville, and Chesterfield. Our overnight shipping labels work from any UPS location, so your location within the St. Louis metro doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in St. Louis?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in St. Louis?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in St. Louis offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in St. Louis?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in St. Louis?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk St. Louis families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Clayton", address: "8201 Forsyth Blvd, Clayton, MO 63105", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Chesterfield", address: "1600 Clarkson Rd, Chesterfield, MO 63017", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Downtown STL", address: "555 Washington Ave, St. Louis, MO 63101", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — St. Charles", address: "1975 Zumbehl Rd, St. Charles, MO 63303", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Florissant", address: "1085 Dunn Rd, Florissant, MO 63031", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Kirkwood", address: "1115 S Kirkwood Rd, Kirkwood, MO 63122", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Ballwin", address: "15009 Manchester Rd, Ballwin, MO 63011", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — St. Louis", address: "75 N Outer 40 Rd, Chesterfield, MO 63017", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — St. Louis",
      address: "4628 Laclede Ave, St. Louis, MO 63108",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Clayton",
      address: "7901 Forsyth Blvd, Clayton, MO 63105",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — St. Charles",
      address: "8001 Veterans Memorial Pkwy, St. Charles, MO 63301",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Florissant",
      address: "4100 N Highway 67, Florissant, MO 63034",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for North County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "STL",
      name: "St. Louis Lambert International Airport",
      distance: "12 miles from downtown St. Louis",
      address: "10701 Lambert International Blvd, St. Louis, MO 63145",
      note: "The primary international airport for St. Louis. Major hub for domestic flights with international connections to Europe, Canada, and Mexico.",
      flightUrl: "https://www.flystl.com/",
      flightLabel: "STL Airport Info",
      featured: true,
    },
    {
      code: "BLV",
      name: "MidAmerica St. Louis Airport",
      distance: "25 miles from downtown St. Louis",
      address: "9656 Air Terminal Dr, Mascoutah, IL 62258",
      note: "Smaller airport across the river in Illinois. Allegiant Air hub with domestic flights. Convenient for Belleville and Southern Illinois residents.",
      flightUrl: "https://www.flymidamerica.com/",
      flightLabel: "MidAmerica Airport",
      featured: false,
    },
  ],
};